import { GALLERY_CONSTANTS } from '@/shared/styles/constants';

export type GalleryImageType = 'landscape' | 'portrait';

// Photo transform types for Cloudinary
export const PHOTO_TRANSFORM_TYPES = {
  FULL_WIDTH: 'f_auto,q_auto,c_limit,w_1080,dpr_auto',
  HALF_WIDTH: 'f_auto,q_auto:eco,c_limit,w_600,dpr_auto',
  AVATAR: 'f_auto,q_auto,c_limit,w_128,dpr_auto',
} as const;

export type PhotoTransformType = keyof typeof PHOTO_TRANSFORM_TYPES;

// Export individual constants for easier imports
export const FULL_WIDTH: PhotoTransformType = 'FULL_WIDTH';
export const HALF_WIDTH: PhotoTransformType = 'HALF_WIDTH';
export const AVATAR: PhotoTransformType = 'AVATAR';

/**
 * Create a resized photo URL using Cloudinary transforms
 */
export function createResizedPhotoUrl(
  url: string,
  transformType: PhotoTransformType,
): string {
  const transforms = PHOTO_TRANSFORM_TYPES[transformType];
  return url.replace('/upload/', `/upload/${transforms}/`);
}

/**
 * Calculate the height for a gallery image based on its type and width
 */
export function getGalleryImageHeight(
  imageType: GalleryImageType,
  width?: number,
): number {
  if (imageType === 'landscape') {
    // For landscape images, calculate height based on aspect ratio
    if (width) {
      return width * (1 / GALLERY_CONSTANTS.LANDSCAPE_ASPECT_RATIO); // Convert width:height ratio to height calculation
    }
    // Fallback to calculated height for default container width
    const defaultLandscapeWidth = GALLERY_CONSTANTS.MAX_WIDTH;
    return (
      defaultLandscapeWidth * (1 / GALLERY_CONSTANTS.LANDSCAPE_ASPECT_RATIO)
    );
  } else {
    // For portrait images, calculate height based on aspect ratio
    if (width) {
      return width * (1 / GALLERY_CONSTANTS.PORTRAIT_ASPECT_RATIO); // Convert width:height ratio to height calculation
    }
    // Fallback to calculated height for default container width
    // Using 48% flex basis from layout constants to calculate default width
    const defaultPortraitWidth = GALLERY_CONSTANTS.MAX_WIDTH * 0.48;
    return defaultPortraitWidth * (1 / GALLERY_CONSTANTS.PORTRAIT_ASPECT_RATIO);
  }
}
